import { Box } from "@mui/material";
import { useState } from "react";
import CoaMappingPanel from "@/components/accounting/CoaMappingPanel";
import DeferredSubscriptionsPanel from "@/components/accounting/deferredSubscriptions/DeferredSubscriptionsPanel";
import JournalEntriesPanel from "@/components/accounting/JournalEntriesPanel";
import NeedsReviewPanel from "@/components/accounting/NeedsReviewPanel";
import PeriodDetail from "@/components/accounting/PeriodDetail";
import PeriodsListPanel from "@/components/accounting/PeriodsListPanel";
import { useNeedsReview } from "@/hooks/useAccounting";

type AccountingTab =
  | "journal-entries"
  | "needs-review"
  | "periods"
  | "coa-mapping"
  | "deferred-subscriptions";

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        appearance: "none",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "10px 4px",
        marginBottom: -1,
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.2px",
        color: active ? "var(--color-rodeo-black)" : "var(--color-graphite)",
        borderBottom: `2px solid ${active ? "var(--color-dark-dust)" : "transparent"}`,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

/**
 * The accounting subledger console. GFG-only — the route and the API both gate on
 * view/FINANCE with a scope of "all", since these are GFG's books across every client.
 *
 * Journal Entries is the blotter; Periods & Posting runs the close cycle — export a batch, confirm
 * QuickBooks accepted it, close the month. Deviation reports land here as they're built.
 */
export default function Accounting() {
  const [tab, setTab] = useState<AccountingTab>("journal-entries");
  // Set when a period reference is clicked; clearing it returns to the list.
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);
  const { data: held = [] } = useNeedsReview();
  const heldCount = held.length;

  const tabs: { key: AccountingTab; label: string }[] = [
    { key: "journal-entries", label: "Journal Entries" },
    {
      key: "needs-review",
      // The count is the whole point of the tab: a held entry is silently excluded from its month,
      // so the only thing that surfaces it is a number that isn't zero.
      label: heldCount > 0 ? `Needs Review (${heldCount})` : "Needs Review",
    },
    { key: "periods", label: "Periods" },
    { key: "coa-mapping", label: "COA Mapper" },
    { key: "deferred-subscriptions", label: "Deferred Subscriptions" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 28,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {tabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </div>

      {tab === "journal-entries" && <JournalEntriesPanel />}
      {tab === "needs-review" && <NeedsReviewPanel />}
      {tab === "periods" &&
        (openPeriod ? (
          <PeriodDetail period={openPeriod} onBack={() => setOpenPeriod(null)} />
        ) : (
          <PeriodsListPanel onOpenPeriod={setOpenPeriod} />
        ))}
      {tab === "coa-mapping" && <CoaMappingPanel />}
      {tab === "deferred-subscriptions" && <DeferredSubscriptionsPanel />}
    </Box>
  );
}
